/**
 * LogisticsDashboard — consulta operacional (Logística).
 *
 * Acesso só leitura. Responde a "Como serão movimentadas as peças que
 * estamos a cotar?".
 *
 * Layout:
 *   ▸ 4 KPIs: RFQs activas com transporte, peso anual estimado,
 *     nº de tipos de caixa em uso, fornecedores activos.
 *   ▸ 3 charts: mix de incoterms (donut), distribuição de tipos de caixa
 *     (donut), categorias de subcontratação (barras).
 *   ▸ 1 tabela: RFQs activas com transporte/embalagem por completar.
 */
import { Link } from 'react-router-dom';
import {
  KpiGrid, DonutChartCard, OperationalTable, StatusBadge,
} from '@/components/dashboard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from 'recharts';
import { Badge } from '@/components/ui/badge';
import { formatNumber } from '@/lib/utils';
import { t } from '@/i18n/translations';

export default function LogisticsDashboard({ data, lang }) {
  const charts = data.charts || {};
  const tables = data.tables || {};

  const subBars = (charts.subcontract_mix || []).map((d) => ({
    name: t(`subcontract_${d.key}`, lang),
    count: d.count,
  }));

  return (
    <div className="space-y-6">
      <KpiGrid kpis={data.kpis || []} lang={lang} />

      <div className="grid gap-4 lg:grid-cols-2">
        <DonutChartCard
          title={t('chart_incoterm_mix', lang)}
          data={(charts.incoterm_mix || []).map((d) => ({
            key: `incoterm_${d.key}`, count: d.count,
          }))}
          lang={lang}
        />
        <DonutChartCard
          title={t('chart_box_type_mix', lang)}
          data={charts.box_type_mix || []}
          lang={lang}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('chart_subcontract_mix', lang)}</CardTitle>
          </CardHeader>
          <CardContent>
            {subBars.every((d) => !d.count) ? (
              <div className="flex h-[220px] items-center justify-center text-sm text-muted-foreground">
                {t('no_data', lang)}
              </div>
            ) : (
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={subBars}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                    <RTooltip
                      contentStyle={{ borderRadius: 8, border: '1px solid hsl(var(--border))' }}
                      formatter={(v) => formatNumber(v, lang)}
                    />
                    <Bar dataKey="count" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <OperationalTable
          title={t('table_logistics_pending', lang)}
          items={tables.logistics_pending || []}
          lang={lang}
          emptyKey="no_logistics_pending"
          columns={[
            { key: 'reference_number', labelKey: 'rfq_ref',
              render: (r) => (
                <Link to={`/rfqs/${r.id}`} className="font-medium hover:underline">
                  {r.reference_number}
                </Link>
              ),
            },
            { key: 'part_designation', labelKey: 'part_designation',
              render: (r) => (
                <span className="text-muted-foreground">{r.part_designation}</span>
              ),
            },
            { key: 'status', labelKey: 'status',
              render: (r) => <StatusBadge status={r.status} lang={lang} />,
            },
            { key: 'missing', labelKey: 'missing_fields',
              render: (r) => (
                <div className="flex flex-wrap gap-1">
                  {(r.missing || []).map((m) => (
                    <Badge key={m} variant="warning" className="text-[10px]">
                      {t(`missing_${m}`, lang)}
                    </Badge>
                  ))}
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
}
